import type { CfFlowResponse, CfFlowScreen, CfFlowStatus } from './api/cloudflareFlowApi';
import { CfEventSubject, CfSharedEventProperties, cfEventProperties, cfEventSubjectOf } from './cfEventProperties';
import { type CfNexusFromStep, type CfStepName, cfStepName } from './cfStepTrail';
import {
  AUTOMATED_CF_INTEGRATION_FLOW,
  INTEGRATE_STEP_ENTRY_POINT,
  type NexusEntryPoint,
} from '../integrationWizard/nexusVocabulary';

/** What a Cloudflare flow screen tells its host when the customer opens Nexus from it. */
export type CfNexusOpening = {
  fromStep?: CfNexusFromStep;
  domain?: string;
  subject?: CfEventSubject;
  errorShown?: string | null;
  /** Named when the prefill decides it, since a token prompt can carry no error. */
  entryPoint?: NexusEntryPoint;
  prefillText?: string;
};

export const CF_FAILED_STATUSES = ['preflight_failed', 'deploy_failed', 'verification_failed'] as const;

export const isFailedStatus = (status: CfFlowStatus): status is (typeof CF_FAILED_STATUSES)[number] =>
  (CF_FAILED_STATUSES as readonly CfFlowStatus[]).includes(status);

export const errorShownOf = (flow: Pick<CfFlowResponse, 'status' | 'errorDetail'>): string | null =>
  isFailedStatus(flow.status) ? flow.errorDetail?.reason ?? flow.status : null;

export type CfNexusOpenedProperties = CfSharedEventProperties & {
  integration_flow: typeof AUTOMATED_CF_INTEGRATION_FLOW;
  from_step: CfNexusFromStep | undefined;
  error_shown: string | null;
};

export const cfNexusOpenedProperties = (
  opening: CfNexusOpening,
  companyId: number | undefined,
): CfNexusOpenedProperties => ({
  ...cfEventProperties(opening.subject ?? { domain: opening.domain ?? '' }, companyId),
  integration_flow: AUTOMATED_CF_INTEGRATION_FLOW,
  from_step: opening.fromStep,
  error_shown: opening.errorShown ?? null,
});

export const nexusEntryPointFor = (errorShown: string | null | undefined): NexusEntryPoint =>
  errorShown != null ? 'verify_step' : INTEGRATE_STEP_ENTRY_POINT;

/** What the host needs to open Nexus from its dock on the screen the customer is looking at. */
export type CfNexusScene = {
  screen: CfFlowScreen;
  step: CfStepName | null;
  subject: CfEventSubject;
  errorShown: string | null;
  prefillText?: string;
};

export const cfNexusSceneOf = (flow: CfFlowResponse, screen: CfFlowScreen): CfNexusScene => ({
  screen,
  step: cfStepName(screen, flow.status),
  subject: cfEventSubjectOf(flow),
  errorShown: errorShownOf(flow),
});

/** Where in the automated Cloudflare flow Nexus was opened, for `nexus_session_started`. */
export type CfNexusStartContext = {
  domain?: string;
  fromStep?: string;
  errorShown?: string | null;
};

export const cfStartContextOf = ({ domain, fromStep, errorShown }: CfNexusOpening): CfNexusStartContext => ({
  domain,
  fromStep,
  errorShown,
});
